// Below this width the search UI is the full-screen sidebar: product rows become
// swipeable carousels and results stay put while the field has a query.
// Matches the mobile predictive-search rules in the CSS.
const PREDICTIVE_SEARCH_MOBILE_MAX_WIDTH = 1024;

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.trendingAndProductsBlock = this.querySelector(
      "[data-quick-trending-products]",
    );
    this.closeStickySearchButton = document.querySelector(
      ".header-search-close",
    );
    this.productToShow = this.dataset.productToShow;
    this.searchDetails = this.querySelector(".search_details");
    this.predictiveSearch = this.querySelector("predictive-search");
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.quickSearchTrending = this.querySelectorAll(
      ".quickSearchTrending .link",
    );
    this.searchResultsWidget = this.getElementsByClassName(
      "quickSearchResultsWidget",
    )[0];

    this.setupEventListeners();
    this.quickSearchTrending.forEach((element) => {
      element.addEventListener(
        "click",
        this.quickSearchTrendingClick.bind(this),
      );
    });
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    document.addEventListener("click", this.onDocClick.bind(this));
    window.addEventListener("resize", this.onResize.bind(this));
    if (this.closeStickySearchButton != null) {
      this.closeStickySearchButton.addEventListener(
        "click",
        this.onCloseStickySearchClick.bind(this),
      );
    }
  }

  handleResponse(data, limit) {
    let htmlMedia = document.createElement("div");
    htmlMedia.innerHTML = data;

    $(htmlMedia)
      .find(`.product:nth-child(n+${Number(limit) + 1})`)
      .remove();
    return $(htmlMedia).html();
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return this.showTrendingAndProducts();

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      // On mobile the input loses focus as soon as the page scrolls (iOS drops
      // the keyboard) or a result is tapped. As long as there is something to
      // search for, the results stay open.
      if (this.shouldKeepResultsOpen()) return;
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  // Only the explicit close controls dismiss a filled mobile search.
  shouldKeepResultsOpen() {
    return this.isMobile() && this.getQuery().length > 0;
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      this.showTrendingAndProducts();
    }
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]',
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.updateViewAllLink(searchTerm);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=query,product,collection,page,article`,
      {
        signal: this.abortController.signal,
      },
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          },
        );
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
        this.updateViewAllLink(searchTerm);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    // The markup this carousel was built on is about to be thrown away.
    this.destroyProductsCarousel();
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    this.setAttribute("open", true);
    this.setLiveRegionResults();
    this.open();
    this.hideTrendingAndProducts();
    if (
      (window.show_multiple_currencies &&
        typeof Currency != "undefined" &&
        Currency.currentCurrency != shopCurrency) ||
      window.show_auto_currency
    ) {
      Currency.convertAll(
        window.shop_currency,
        $("#currencies .active").attr("data-currency"),
        "span.money",
        "money_format",
      );
    }
    window.sharedFunctions?.swapHoverVideoProductCard();
    this.initProductsCarousel();
  }

  connectedCallback() {
    // On mobile the trending row is on screen as soon as the search sidebar
    // opens, before the input is ever focused.
    this.initCarousels();
    // The element gets moved between the desktop and mobile headers.
    this.trendingCarousel?.update();

    // This file is deferred in <head> while swiper-bundle.js sits at the end of
    // <body>, so Swiper does not exist yet on that first pass. base.css also
    // lands late (media="print" swap) and can change the row's width after it
    // was measured. Retry and re-measure as the page settles, rather than
    // waiting for a resize.
    if (!this.settleBound) {
      this.settleBound = true;
      const settle = () => {
        this.initCarousels();
        this.trendingCarousel?.update();
      };

      document.addEventListener("DOMContentLoaded", settle, { once: true });
      window.addEventListener("load", settle, { once: true });
    }
  }

  initCarousels() {
    this.initTrendingCarousel();
    this.initProductsCarousel();
  }

  isMobile() {
    // Antes: window.innerWidth a cada chamada. innerWidth forca layout sincrono quando o
    // layout esta sujo, e isto e chamado de varios pontos (shouldKeepResultsOpen,
    // buildMobileCarousel, onResize) — somava 133 ms de self time numa funcao de uma
    // linha. O valor so muda no resize, entao fica em cache e onResize() o invalida.
    // Mantem innerWidth de proposito: matchMedia (max-width: 1024px) e innerWidth nao
    // concordam na fronteira no desktop, porque a barra de rolagem conta num e no outro nao.
    if (this._larguraJanela === undefined) {
      this._larguraJanela = window.innerWidth;
    }
    return this._larguraJanela <= PREDICTIVE_SEARCH_MOBILE_MAX_WIDTH;
  }

  // Builds a carousel out of a product row the theme already renders. Swiper is
  // pointed at the existing class names instead of adding swiper-wrapper /
  // swiper-slide, which would also reach the desktop layouts.
  buildMobileCarousel(wrapper, wrapperClass) {
    if (!this.isMobile() || typeof Swiper === "undefined") return null;

    const container = wrapper?.parentElement;

    if (!container || container.swiper) return null;
    // Hidden rows measure 0 and would get 0-wide slides: leave them for the next
    // show / resize, when they are on screen.
    if (!container.clientWidth) return null;
    // A single product has nothing to swipe through, so leave the row alone.
    if (wrapper.querySelectorAll(":scope > .product").length < 2) return null;

    return new Swiper(container, {
      wrapperClass: wrapperClass,
      slideClass: "product",
      slidesPerView: 1.4,
      spaceBetween: 10,
    });
  }

  destroyCarousel(carousel, cleanStyles = false) {
    if (carousel && !carousel.destroyed) carousel.destroy(true, cleanStyles);
    return null;
  }

  initProductsCarousel() {
    this.productsCarousel =
      this.productsCarousel ||
      this.buildMobileCarousel(
        this.querySelector("#predictive-search-results-list > .products-list"),
        "products-list",
      );
  }

  destroyProductsCarousel(cleanStyles = false) {
    this.productsCarousel = this.destroyCarousel(
      this.productsCarousel,
      cleanStyles,
    );
  }

  initTrendingCarousel() {
    this.trendingCarousel =
      this.trendingCarousel ||
      this.buildMobileCarousel(
        this.querySelector(".quickSearchProduct .products-grid"),
        "products-grid",
      );
  }

  onResize() {
    this._larguraJanela = window.innerWidth;

    if (this.isMobile()) {
      this.initCarousels();
      return;
    }

    // Hand both rows back their own layout, inline slide widths included.
    this.destroyProductsCarousel(true);
    this.trendingCarousel = this.destroyCarousel(this.trendingCarousel, true);
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document
        .querySelector('[class^="header-navigation"]')
        .getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }

  showTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return;
    this.trendingAndProductsBlock.classList.add("is-show");
    this.trendingAndProductsBlock.classList.remove("hidden");
    this.initTrendingCarousel();
    // The sidebar can be moved between headers, so re-measure on every show.
    this.trendingCarousel?.update();
  }

  hideTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return;

    this.trendingAndProductsBlock.classList.remove("is-show");
    this.trendingAndProductsBlock.classList.add("hidden");
  }

  onDocClick(e) {
    const $target = e.target,
      isInModal = this.contains($target),
      // closest() already covers the element itself.
      isCloseControl = !!$target.closest?.(
        ".header-search-popup-close, .header-search-close, [data-search-close-sidebar]",
      );

    // A filled mobile search is only dismissed by those controls.
    if (!isCloseControl && this.shouldKeepResultsOpen()) return;

    if (!isInModal || isCloseControl) {
      this.close();
      this.hideTrendingAndProducts();
    }
  }

  onCloseStickySearchClick() {
    this.close();
    this.hideTrendingAndProducts();
    document.querySelector("body")?.classList.remove("sticky-search-open");
    document.querySelector("body")?.classList.remove("sticky-search-menu-open");
    this.closest('[class*="section-header-"]')?.classList.remove(
      "sticky-search-menu-open",
    );
    this.closest('[class*="section-header-"]')?.classList.remove(
      "sticky-search-menu-custom-open",
    );
  }

  updateViewAllLink(searchTerm) {
    const qsViewAllLink = document.querySelector("[data-qs-view-all-link]");
    if (!qsViewAllLink) return;
    // const linkTotal = `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product`
    const linkTotal = `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&options%5Bprefix%5D=last&type=product`;

    qsViewAllLink.href = linkTotal;
    return this.getTotalResults(linkTotal).then((count) => {
      qsViewAllLink.innerHTML = qsViewAllLink.innerHTML.replace(
        "()",
        `(${count})`,
      );
    });
  }

  getTotalResults(url) {
    return fetch(url)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const totalCount = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector('[id^="SearchSection"]').dataset.searchCount;
        return totalCount;
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  quickSearchTrendingClick(event) {
    window.location = event.currentTarget.getAttribute("href");
  }
}

customElements.define("predictive-search", PredictiveSearch);
